import {scheduleIdleAnalytics} from '../../utils/common-utils'

const clonePayload = (payload) => {
    if (typeof window.structuredClone === 'function') {
        return window.structuredClone(payload)
    }

    if (payload === undefined) return undefined
    return JSON.parse(JSON.stringify(payload))
}

const toBatches = (hits, size) => {
    const batches = []

    for (let index = 0; index < hits.length; index += size) {
        // Each event gets its own snapshot. The tag manager decorates product objects,
        // so sharing a batch between events would make later payloads mutable.
        batches.push(clonePayload(hits.slice(index, index + size)))
    }

    return batches
}

export const createProductListAnalyticsTasks = ({
    hits = [],
    breadcrumb = '',
    category,
    sortLabel = '',
    isMobile,
    tagManager,
    startIndex = 0
}) => {
    const firstIndex = Number(startIndex) || 0
    const indexedHits = hits.map((hit, index) => ({...hit, index: firstIndex + index}))
    const capturedBreadcrumb = breadcrumb ? breadcrumb.split(' / ').join('/') : ''
    const capturedCategory = clonePayload(category)
    const impressionBatches = toBatches(indexedHits, 4)
    const viewListBatches = toBatches(indexedHits, isMobile ? 4 : 6)
    const runWithDataLayer = (callback) => () => {
        window.__dataLayerLoaded = true
        try {
            callback()
        } finally {
            window.__dataLayerLoaded = false
        }
    }

    return [
        ...impressionBatches.map((batch) =>
            runWithDataLayer(() =>
                tagManager.productImpression(batch, capturedBreadcrumb, capturedCategory)
            )
        ),
        ...viewListBatches.map((batch) =>
            runWithDataLayer(() => tagManager.viewItemLists(batch, sortLabel))
        )
    ]
}

/**
 * Runs one analytics operation per idle callback. Remaining operations are
 * flushed in order on pagehide so navigation cannot discard queued tracking.
 */
export const scheduleAnalyticsTasks = (
    tasks,
    {shouldRun = () => true, onComplete = () => {}, schedule = scheduleIdleAnalytics} = {}
) => {
    let nextTask = 0
    let scheduledHandle
    let stopped = false
    let completed = false

    const finish = () => {
        if (completed) return
        completed = true
        window.removeEventListener('pagehide', flush)
        onComplete()
    }

    const executeNext = () => {
        try {
            tasks[nextTask]?.()
        } catch (error) {
            // One malformed analytics event must not discard the rest of the queue.
            console.error('Product list analytics task failed', error)
        }
        nextTask += 1
    }

    const runNext = () => {
        if (stopped || !shouldRun()) {
            return
        }

        executeNext()

        if (nextTask < tasks.length) {
            scheduledHandle = schedule(runNext, 0)
        } else {
            finish()
        }
    }

    const flush = () => {
        scheduledHandle?.cancel?.()

        while (!stopped && shouldRun() && nextTask < tasks.length) {
            executeNext()
        }

        if (!stopped && nextTask === tasks.length) {
            finish()
        }
    }

    const cancel = () => {
        stopped = true
        scheduledHandle?.cancel?.()
        window.removeEventListener('pagehide', flush)
    }

    if (!tasks.length) {
        finish()
    } else {
        window.addEventListener('pagehide', flush)
        scheduledHandle = schedule(runNext, 0)
    }

    return {cancel, flush}
}
